import React, { useState } from "react"
import { MinusIcon, PlusIcon } from "components/SVG"
import {
  accordion,
  accordionHeader,
  accordionContent,
  accordionContentShow,
  accordionIcon,
  filterPanelHeadline,
  panelClearButton,
  panelClearButtonCopy,
  filterItem,
  filterItemCopy,
  filterCheckbox,
  filterCheckboxChecked,
  accordionStack,
} from "./PLPFilters.css"
import type { FilterInputType, FilterItemType } from "./types"

type AccordionProps = {
  items: FilterItemType["items"]
  filterState: FilterInputType[]
  toggleFilter: (input: FilterInputType, index: number) => void
  hasFiltersForCategory: (index: number) => boolean
  removeFilters: (index: number) => void
}

export const PLPAccordion: React.FC<AccordionProps> = ({
  items,
  filterState,
  toggleFilter,
  hasFiltersForCategory,
  removeFilters,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setActiveIndex((prevIndex) => (prevIndex === index ? null : index))
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      toggleAccordion(index)
    }
  }

  const handleCheckboxKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    item: FilterInputType,
    index: number
  ) => {
    if (event.key === "Enter") {
      event.preventDefault()
      toggleFilter(item, index)
    }
  }

  return (
    <>
      {items.map(({ label, subItems, slug }, index) => {
        const isActive = activeIndex === index
        return (
          <div key={slug} className={accordion}>
            <div
              className={accordionHeader}
              onClick={(e) => {
                if (
                  !(e.target as HTMLElement).closest(`.${panelClearButton}`)
                ) {
                  toggleAccordion(index)
                }
              }}
              onKeyDown={(e) => handleKeyDown(e, index)}
              role="button"
              tabIndex={0}
              aria-expanded={isActive}
              aria-controls={`accordion-content-${index}`}
            >
              {isActive ? (
                <MinusIcon
                  viewBox="3 3 18 18"
                  width="18"
                  height="18"
                  className={accordionIcon}
                />
              ) : (
                <PlusIcon
                  viewBox="3 3 18 18"
                  width="18"
                  height="18"
                  className={accordionIcon}
                />
              )}
              <h2 className={filterPanelHeadline}>{label}</h2>
              {hasFiltersForCategory(index) && (
                <button
                  type="button"
                  className={panelClearButton}
                  onClick={() => removeFilters(index)}
                >
                  <span className={panelClearButtonCopy}>Clear</span>
                </button>
              )}
            </div>
            <div
              className={`${accordionContent} ${
                isActive ? accordionContentShow : ""
              }`}
            >
              <div className={accordionStack}>
                {subItems.map((item, catIndex) => {
                  const isSelected = !!filterState.find(
                    (el) => el.slug === item.slug && el.subCategory === slug
                  )
                  const count = item.count ?? 0
                  const countText = ` (${count})`
                  const key = `${label}-${item.slug}-${item.label}-${catIndex}`

                  return (
                    <label key={key} className={filterItem}>
                      <input
                        type="checkbox"
                        className={
                          isSelected ? filterCheckboxChecked : filterCheckbox
                        }
                        checked={isSelected}
                        onChange={() => toggleFilter(item, index)}
                        onKeyDown={(e) => handleCheckboxKeyDown(e, item, index)}
                      />
                      <span
                        className={filterItemCopy}
                      >{`${item.label}${countText}`}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
